import React, { useState } from 'react';
import { motion, AnimatePresence} from 'framer-motion';
import { Award, Users, Building2, Heart, Target, Eye } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedWidgets from '../components/FixedWidgets';
import hospital from '../assets/hospital.png';
import drsantosh from '../assets/drsantosh.jpeg';
import isoCert from "../assets/iso.png";
import cert from "../assets/certificate.jpeg";

const AboutPage = () => {

  const [selectedCert, setSelectedCert] = useState(null);
  
    const certifications = [
    {
      name: "ISO 9001:2015",
      image: isoCert,
      certi: cert
    },
  ]; 

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const stats = [
    { number: '25+', label: 'Hospital Beds', icon: Building2 },
    { number: '10+', label: 'Expert Doctors', icon: Users },
    { number: '24/7', label: 'Emergency Care', icon: Heart },
    { number: '8+', label: 'Departments', icon: Award }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FixedWidgets />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden mt-20">
        <motion.div
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 to-[#4A90A4]/80 z-10" />
          <img
            src="https://images.unsplash.com/photo-1710074213379-2a9c2653046a"
            alt="About Us"
            className="w-full h-full object-cover"
          />
        </motion.div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold text-white mb-6"
          >
            About <span className="text-[#6FCF97]">Suvaidyam Hospital</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-200 max-w-3xl mx-auto"
          >
            A comprehensive healthcare facility committed to quality care and safety
          </motion.p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.05 }}
                className="bg-gradient-to-br from-[#4A90A4] to-[#6FCF97] p-8 rounded-3xl text-center text-white shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-4"
                >
                  <stat.icon size={32} />
                </motion.div>
                <h3 className="text-4xl font-bold mb-2">{stat.number}</h3>
                <p className="text-lg opacity-90">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              {...fadeInUp}
              className="bg-white p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 text-center flex flex-col items-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-[#4A90A4] to-[#6FCF97] rounded-2xl flex items-center justify-center mb-6">
                <Eye className="text-white" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h2>
              <p className="text-gray-600 leading-relaxed text-lg max-w-xl">
                To be the most trusted healthcare provider in the region, delivering world-class medical services with compassion, innovation, and excellence. We strive to set new standards in patient care and medical education.
              </p>
            </motion.div>

            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.2 }}
              className="bg-white p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 text-center flex flex-col items-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-[#6FCF97] to-[#4A90A4] rounded-2xl flex items-center justify-center mb-6">
                <Target className="text-white" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed text-lg max-w-xl">
                To provide accessible, affordable, and quality healthcare services to all. We are committed to advancing medical knowledge through research and education while maintaining the highest standards of patient safety and satisfaction.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
               ISO Certified Suvaidyam Multispeciality Hospital & Research Institute
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Suvaidyam Hospital is a 30+ bed multispecialty hospital and research institute. We are committed to delivering comprehensive healthcare services with a focus on quality, safety, and patient satisfaction.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Our state-of-the-art facility is equipped with the latest medical technology and staffed by experienced healthcare professionals dedicated to providing personalized care to every patient.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                We offer 24/7 emergency services like critical care units, trauma centre and a wide range of specialized medical services under one roof round the clock.
              </p>
            </motion.div>

            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.2 }}
              whileHover={{ scale: 1.02 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={hospital}
                  alt="Hospital Facility"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/50 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#4A90A4] to-[#6FCF97] rounded-3xl blur-2xl opacity-30" />
                <img
                    src={drsantosh}
                    alt="Dr. Santosh"
                    className="
                      relative z-10
                      w-[26rem]
                      sm:w-[30rem]
                      md:w-[39rem]
                      lg:w-[45rem]
                      h-auto
                      rounded-3xl
                      shadow-2xl
                      object-cover
                    "
                  />

              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                President & <span className="text-[#4A90A4]">CEO Message</span>
              </h2>

              <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                DR. Santosh Vastrad
              </h3>

              <p className="text-[#6FCF97] font-semibold mb-6">
                Consultant Physician, Diabetes and Thyroid Specialist
              </p>

              <p className="text-gray-600 leading-relaxed mb-6">
                Our success is built on the tireless dedication of those who show up every day to make a difference. 
                To us, the 'white coat' isn't just a uniform—it is a symbol of our unwavering commitment to service. 
                We believe that by devoting ourselves to the well-being of others, we find our own greatest sense of purpose and fulfillment.
              </p>

              <p className="text-gray-600 leading-relaxed mb-6">
                <strong>Dr. Santosh Vastrad</strong> is an eminent <strong>Consultant Physician, Diabetes and Thyroid Specialist</strong> with 
                an illustrious career dedicated to excellence in internal medicine. With extensive experience 
                in managing complex metabolic disorders, he has successfully treated thousands of patients, specializing in : 
                <strong> Advanced Diabetes Management</strong>,<strong>Thyroid & Endocrine Disorders</strong>,<strong>Comprehensive Internal Medicine</strong> &  <strong>Preventative Care</strong>
              </p>

              

              {/* Qualifications */}
              <div className="mt-8 bg-gray-50 border border-gray-200 rounded-2xl p-6">
                <h4 className="text-xl font-bold text-gray-800 mb-4">
                  Education & Training
                </h4>   
                <ul className="space-y-2 text-gray-700 list-disc list-inside">
                  <li>MBBS – KMC Hubli (2003)</li>
                  <li>MD (Int. Med) (Department) – BLDE Medical College Vijapur (2008)</li>
                  <li>FDE (Endocrinology) – Manipal Hospital Bangalore (2010)</li>
                  
                </ul>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gradient-to-br from-[#1e3a5f] to-[#2d5a7b] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="text-[#6FCF97]">Certifications</span>
            </h2>
            <p className="text-gray-300 text-lg">
              Recognized and certified by leading international standards organizations
            </p>
          </motion.div>

          <div className="flex justify-center">

            {certifications.map((cert, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
            whileHover={{ scale: 1.05, y: -5 }}
            className="bg-white/10 backdrop-blur-md p-10 rounded-xl text-center border border-white/20 hover:bg-white/20 transition-all duration-300 w-full"
          >
            <img
              src={cert.certi}
              alt={cert.name}
              className="mx-auto mb-4 h-24 object-contain rounded-md"
            />

            <button
              onClick={() => setSelectedCert(cert.image)}
              className="mt-3 text-[#6FCF97] underline hover:text-white transition"
            >
              View Certificate
            </button>
          </motion.div>
        ))}



      <AnimatePresence>
              {selectedCert && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setSelectedCert(null)}
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
                >
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 120 }}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-full max-w-5xl h-[85vh] bg-white rounded-2xl shadow-2xl flex items-center justify-center p-6"
                  >
                    {/* Close Button */}
                    <button
                      onClick={() => setSelectedCert(null)}
                      className="absolute top-4 right-4 w-11 h-11 bg-gray-900 text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition"
                    >
                      ✕
                    </button>

                    {/* Certificate Image */}
                    <img
                      src={selectedCert}
                      alt="Certificate"
                      className="max-w-full max-h-full object-contain rounded-lg"
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;